// Refund and chargeback policy for SubsWall (Dr. Gotthilf LLC, Wyoming).
// Stripe Connect onboarding requires a public refund URL.

export default function Refunds(): JSX.Element {
  return (
    <article className="legal-page">
      <h1>Refund &amp; Chargeback Policy</h1>
      <p className="legal-meta">
        Effective date: 2026-05-30 · Last updated: 2026-05-30
      </p>

      <p>
        <em>
          This document is the publicly-posted refund and chargeback policy
          for SubsWall. It is not legal advice. This Policy is incorporated
          into our <a href="/terms">Terms of Service</a> by reference.
        </em>
      </p>

      <h2>1. Donations are voluntary tips</h2>
      <p>
        Donations sent through the SubsWall Platform (operated by{" "}
        <strong>Dr. Gotthilf LLC</strong>, Sheridan, Wyoming) are voluntary
        tips from a Donor to a Creator. They are <strong>not</strong> payment
        for goods, services, investment returns, or anything of measurable
        value. Donations are <strong>generally non-refundable</strong>.
      </p>

      <h2>2. Discretionary refunds to Donors</h2>
      <p>
        We may, at our sole discretion, refund a Donation in the following
        situations:
      </p>
      <ul>
        <li>An obvious typographical error in the Donation amount (for
          example, $1,000 instead of $10) reported within 24 hours.</li>
        <li>Unauthorized use of a payment method (for example, a child
          using a parent's card without permission) reported within 7 days
          of the charge, with reasonable proof.</li>
        <li>A duplicate Donation caused by a technical fault on the
          Platform.</li>
        <li>A Donation made under demonstrable fraud or impersonation of
          the receiving Creator.</li>
      </ul>
      <p>
        To request a discretionary refund, email{" "}
        <a href="mailto:support@subswall.com">support@subswall.com</a> from
        the email used at checkout (if any) within seven (7) days of the
        Donation, including the date, amount, last four digits of the card,
        and a short description of the reason. We will respond within two (2)
        business days. Approved refunds are processed by Stripe and typically
        appear on the original payment method within five to ten (5-10)
        business days, depending on your card issuer.
      </p>

      <h2>3. Streamer-initiated refunds</h2>
      <p>
        Creators may refund a Donation they received from their dashboard
        within thirty (30) days of receipt. Stripe initiates the refund to
        the Donor's original payment method. Approved refunds are returned
        in full to the Donor. The Stripe processing fee originally charged
        on the Donation is not returned to the Creator (see section 5). The
        4.9% platform application fee originally retained by the Company is
        reversed where Stripe permits.
      </p>

      <h2>4. Chargebacks</h2>
      <p>
        A chargeback is a dispute opened by a Donor with their card issuer.
        Chargebacks are governed by card-network rules and Stripe's dispute
        process, not by the Company.
      </p>
      <p>
        <strong>How chargebacks flow on SubsWall.</strong> Donations are
        processed using Stripe Connect's destination-charge model. Under
        Stripe Connect, <strong>chargebacks are debited from the Creator's
        Connected Account, not from the Company</strong>. By accepting
        Donations through the Platform, the Creator agrees to bear the full
        cost of any chargeback on Donations they received, including the
        disputed amount and any Stripe dispute fee, consistent with the
        Stripe Connected Account Agreement.
      </p>
      <p>
        <strong>Disputing a chargeback.</strong> If a chargeback is opened
        against a Donation you received, you will receive a notification.
        Stripe provides a window in which you can submit evidence (a short
        narrative, screenshots of the overlay, the original Donation
        message, the Donor's display name, etc.). The Company can assist on
        a best-effort basis but ultimately the card network decides the
        dispute.
      </p>
      <p>
        <strong>Repeat chargebacks.</strong> A Donor account that initiates
        repeated chargebacks instead of contacting us first may be blocked
        from future Donations on the Platform.
      </p>

      <h2>5. Fees on refunds</h2>
      <p>
        Stripe processing fees (typically 2.9% + $0.30 per successful card
        charge, set by Stripe) are <strong>not refunded</strong> to the
        Creator when a Donation is refunded or charged back. This is a
        Stripe policy that the Company cannot override. The Company's 4.9%
        platform application fee is reversed where Stripe's APIs permit.
      </p>

      <h2>6. Currency and FX</h2>
      <p>
        Refunds are issued in the same currency as the original Donation.
        If the Donor's card was billed in a different currency from the
        Donation currency, the Donor's card issuer applies the FX rate in
        effect on the refund date, which may differ from the original
        charge date. Small residual differences are not reimbursed by the
        Company or by the Creator.
      </p>

      <h2>7. Contact</h2>
      <p>
        Email: <a href="mailto:support@subswall.com">support@subswall.com</a><br />
        Mail: Dr. Gotthilf LLC, 30 N Gould St Ste R, Sheridan, WY 82801, USA
      </p>
    </article>
  );
}
